import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import qs from 'qs';
import { trackPromise } from 'react-promise-tracker';
import { api } from '../../../../../lib';
import History from '../../../../../utils/History';
import { getValidData } from '../../../RevisaoPrecos/utils';
import { NegotiationDataItemProps, NegotitationEditDatapointProps, SuppliersResponseProps } from '../NegociacaoFornecedor';
import { GetNegociacoesItemProps } from '../NegociacoesSalvas/NegociacoesSalvas.services';
import type { EscolhasDaVerbaNoArquivo } from '../NegociacaoFornecedor/verbaNoArquivo';
import type { ResumoDaImportacao } from './resumoDaImportacao';

export async function GETNegotiationDetails(ipaNegotiationId: string): Promise<GetNegociacoesItemProps> {
  return trackPromise(
    api({
      url: '/negotiations/info',
      method: 'GET',
      expectToken: true,
      params: { ipaNegotiationId },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    })
      .then(resp => resp.data)
      .catch(err => {
        const errorMessage = err.response.data._embedded.errors[0].message;
        if (errorMessage === 'Negociação não encontrada.') {
          History.push('/ipa/negociacoes/salvas');
        }
      }),
    'negotiation/get-details',
  );
}

export async function DELETENegotiation(id: string) {
  return trackPromise(
    api({
      url: '/negotiations',
      method: 'DELETE',
      expectToken: true,
      data: { negotiationId: id, selectedAll: true },
    }),
    'negotiation/delete-negotiation',
  );
}

export async function PATCHCloseNegotiation(id: string) {
  return trackPromise(
    api({
      url: '/negotiations/closed',
      method: 'PATCH',
      expectToken: true,
      data: { id },
    }),
    'negotiation/close-negotiation',
  );
}

export async function PATCHReopenNegotiation(id: string) {
  return trackPromise(
    api({
      url: '/negotiations/reopen',
      method: 'PATCH',
      expectToken: true,
      data: { id },
    }),
    'negotiation/reopen-negotiation',
  );
}

export async function GETDatapointsDetalhesDaNegociacao(params: Record<string, unknown>): Promise<SuppliersResponseProps> {
  return trackPromise<AxiosResponse<SuppliersResponseProps>>(
    api({
      url: '/negotiations/details',
      method: 'GET',
      expectToken: true,
      params,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }),
    'negotiations/details/get-datapoints',
  ).then(res => res.data);
}

export type EditarNegociacaoProps = {
  negotiationId: string;
  name: string;
  supplier: string;
  startDate: Date;
  endDate?: Date;
};

export async function editarNegociacao(data: EditarNegociacaoProps): Promise<AxiosResponse<unknown>> {
  return trackPromise(
    api({
      url: '/negotiations/edit',
      method: 'PUT',
      expectToken: true,
      data,
    }),
  );
}

export async function PATCHNegotiationDetailsSaveEditedDatapoint(datapoint: NegotitationEditDatapointProps): Promise<NegotiationDataItemProps> {
  const validData = getValidData(datapoint);

  // O filtro acima descarta nulo, e apagar a verba E mandar nulo: por isso os dois campos dela
  // entram DEPOIS dele, e por PRESENCA da chave da origem, nunca por ela ter valor.
  const mexeuNaVerba = Object.prototype.hasOwnProperty.call(datapoint, 'sellinAllowanceOrigin') && datapoint.sellinAllowanceOrigin !== undefined;

  const verba = mexeuNaVerba ? { sellinAllowance: datapoint.sellinAllowance ?? null, sellinAllowanceOrigin: datapoint.sellinAllowanceOrigin ?? null } : {};

  return trackPromise(
    api({
      url: '/negotiations/datapoints',
      method: 'PUT',
      expectToken: true,
      data: { ...validData, ...verba },
    }).then(resp => resp.data),
    `negotiation-edit-datapoint-${datapoint.id}`,
  );
}

export async function DELETENegotiationDatapoint(data: { negotiationId: string; datapoints: Record<'excludedIds' | 'selectedIds', string[]> }) {
  return trackPromise(
    api({
      url: '/negotiations',
      method: 'DELETE',
      expectToken: true,
      data: {
        ...data,
        selectedAll: false,
      },
    }),
    'negotiation/delete-negotiation',
  );
}

export async function POSTDownloadNegotiation(negotiationId: string) {
  const currentDate = new Date();
  const formattedDate = format(currentDate, 'dd-MM-yyyy');
  const downloadTableName = `${formattedDate}_ipa_negociacao_fornecedor.csv`;
  return trackPromise(
    api({
      url: '/negotiations/download',
      method: 'POST',
      expectToken: true,
      params: { negotiationId },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
      responseType: 'arraybuffer',
    })
      .then(res => {
        if (res.status === 200) {
          return res.data;
        }
        return res;
      })
      .then(resp => {
        let download = resp;
        if (!download.status) {
          const blob = new Blob([download]);
          const hiddenElement = document.createElement('a');
          hiddenElement.href = window.URL.createObjectURL(blob);
          hiddenElement.target = '_blank';
          hiddenElement.download = downloadTableName;
          hiddenElement.click();
          download = null;
        }
        return download;
      }),
    'download-datapoints-negociacao',
  );
}

const TIPO_DA_PLANILHA = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const NOME_DE_RECURSO_DO_RESUMO = 'resumo-ao-fornecedor.xlsx';

/** O cabeçalho só chega ao JavaScript se a resposta o expuser, e um proxy pode não repassar. */
function nomeDoArquivoNoCabecalho(cabecalho: string | undefined): string {
  if (!cabecalho) return NOME_DE_RECURSO_DO_RESUMO;
  const casamento = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cabecalho);
  if (!casamento) return NOME_DE_RECURSO_DO_RESUMO;
  try {
    return decodeURIComponent(casamento[1].trim());
  } catch {
    // Um `%` literal no nome faz `decodeURIComponent` lançar, e o arquivo chegou inteiro.
    return casamento[1].trim() || NOME_DE_RECURSO_DO_RESUMO;
  }
}

/** Em `colunas`, **ausente pede o conjunto padrão e lista vazia pede só as fixas**. */
export async function POSTResumoAoFornecedorFile(ipaNegotiationId: string, colunas?: string[]) {
  return trackPromise(
    api({
      url: '/negotiations/supplier-summary/file',
      method: 'POST',
      expectToken: true,
      params: { ipaNegotiationId },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
      data: colunas === undefined ? undefined : { colunas },
      responseType: 'arraybuffer',
    }).then(res => {
      // Resposta vazia não vira arquivo: um .xlsx de zero byte iria ao fornecedor sem ninguém ver.
      if (!res.data || (res.data as ArrayBuffer).byteLength === 0) {
        throw new Error('resumo ao fornecedor veio vazio');
      }

      const blob = new Blob([res.data], { type: TIPO_DA_PLANILHA });
      const endereco = window.URL.createObjectURL(blob);
      const elemento = document.createElement('a');
      elemento.href = endereco;
      elemento.download = nomeDoArquivoNoCabecalho(res.headers?.['content-disposition']);
      elemento.click();
      // Revogar no mesmo passo do clique aborta o download de arquivo grande em alguns navegadores.
      window.setTimeout(() => window.URL.revokeObjectURL(endereco), 0);

      return blob;
    }),
    'resumo-ao-fornecedor-file',
  );
}

/**
 * Sem conteúdo vira ausência, e nunca resumo zerado. **Sem chamador desde que o modal de
 * confirmação da importação saiu da tela**, e fica de propósito.
 */
export async function GETResumoDaImportacao(ipaNegotiationId: string): Promise<ResumoDaImportacao | null> {
  return trackPromise(
    api({
      url: '/negotiations/import-summary',
      method: 'GET',
      expectToken: true,
      params: { ipaNegotiationId },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(res => {
      // 204 chega com corpo vazio, que o axios entrega como texto vazio.
      if (res.status === 204 || !res.data) return null;
      return res.data as ResumoDaImportacao;
    }),
    'negotiation/import-summary',
  );
}

export type NegotiationUploadStartResponse = {
  status?: string;
  message?: string;
  title?: string;
  description?: string;
};

/** A parte das escolhas sobre a verba é **opcional** dos dois lados: sem leitura, não houve escolha. */
export async function POSTUploadNegotiationProductsFile(
  negotiationId: string,
  file: File,
  escolhasDaVerba?: EscolhasDaVerbaNoArquivo | null,
): Promise<AxiosResponse<NegotiationUploadStartResponse>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('uid', negotiationId);

  if (escolhasDaVerba) {
    formData.append('data', new Blob([JSON.stringify(escolhasDaVerba)], { type: 'application/json' }));
  }

  return trackPromise(
    api({
      url: '/negotiations/datapoints/upload',
      method: 'POST',
      expectToken: true,
      data: formData,
    }),
    'negotiation/upload-products-file',
  );
}

export async function desfazerAlteracaoDePrecos(data: Partial<NegotiationDataItemProps>) {
  const payload = {
    ...data,
    ...(data.visionSaved === 'CLUSTER' &&
      data.cluster?.clusterId && {
        clusterId: data.cluster.clusterId,
      }),
    ...(data.visionSaved && {
      visionSaved: data.visionSaved,
    }),
  };

  const res = await api.put<NegotiationDataItemProps>('negotiations/undo-datapoint-changes', payload, {
    expectToken: true,
  });
  return res.data;
}
